import React, { useState } from "react";

import { Button } from "@/components/primitives/Button";
import { Input } from "@/components/primitives/Input";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/theme";

export const LoginPage: React.FC = () => {
  const { login, error } = useAuth();
  const { theme } = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setIsLoading(true);
    const success = await login(username, password);
    setIsLoading(false);
    if (success) {
      window.location.href = "/";
    } else {
      setFormError(error || "Invalid credentials");
    }
  };

  const logoSrc = theme === "dark" ? "/logo-dark.svg" : "/logo-light.svg";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-4 dark:bg-slate-950">
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-softnu-300/30 blur-3xl dark:bg-softnu-500/20" />
        <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-purple-300/20 blur-3xl dark:bg-purple-500/10" />
      </div>

      <div className="relative w-full max-w-md animate-fade-in-up">
        <div className="mb-8 text-center">
          <img src={logoSrc} alt="Service Health Monitor" className="mx-auto h-16" />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl dark:border-slate-800 dark:bg-slate-900">
          <h1 className="mb-2 text-center text-2xl font-bold text-slate-900 dark:text-white">
            Welcome back
          </h1>
          <p className="mb-6 text-center text-slate-500 dark:text-slate-400">
            Sign in to manage your health flows
          </p>

          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            {formError && (
              <div
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
                role="alert"
              >
                {formError}
              </div>
            )}

            <Input
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />

            <Button type="submit" size="lg" isLoading={isLoading} className="mt-2 w-full">
              Sign in
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          © {new Date().getFullYear()} Service Health Monitor
        </p>
      </div>
    </div>
  );
};
